/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { catchError, from, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { fromFetch } from "rxjs/fetch";
import { getAuthService } from "./auth.service";
import { toast } from "components/ToastProvider/ToastProvider";
import { env } from "env";
import { captureException } from "@sentry/react";

export type ReqOptions = {
  reqScope?: "org" | "client";
  noClientId?: boolean;
  clientId?: number;
  noAuth?: boolean;
  includeCredentials?: boolean;
  cors?: RequestMode;
  raw?: boolean;
  res?: "json" | "text" | "null" | "blob";
  type?: "application/json" | "application/binary" | "application/text";
  headers?: { [key in string]: string };
  errorHandlers?: {
    [key in string]: (err: object) => void;
  };
  errorHandler?: (res: Response, err: ApiError) => void | Observable<any>;
  nullOnError?: boolean;
};

class ApiService {
  private defaultOptions: ReqOptions;
  constructor(initOptions: ReqOptions) {
    this.defaultOptions = initOptions;
  }

  public setDefaultOptions = (options: ReqOptions) => {
    this.defaultOptions = options;
  };

  public apiCall$ = <Res>(
    url: string,
    method: "GET" | "POST" | "DELETE" | "PATCH" | "PUT",
    params?: { [key in string]: string | number | object | boolean } | null,
    body?: object | null,
    passedOptions?: ReqOptions,
  ): Observable<Res | null> => {
    const options = Object.assign({}, this.defaultOptions, passedOptions ?? {});
    const reqUrl = new URL(url, env.VITE_API_BASEURL ?? window.location.origin);
    if (params) {
      Object.keys(params).forEach((key) => {
        reqUrl.searchParams.append(key, `${params[key]}`);
      });
    }
    const cancelToken$ = new Subject<boolean>();

    const req = new Request(reqUrl, {
      method,
      mode: options.cors,
      credentials: options.includeCredentials ? "include" : undefined,
      body: body ? (body instanceof FormData || options?.raw ? (body as never) : JSON.stringify(body)) : null,
    });
    req.headers.set("Content-Type", options?.type || "application/json");

    if (options.headers) {
      Object.keys(options.headers).forEach(
        (header) => options.headers && req.headers.set(header, options.headers[header] as string),
      );
    }
    const isMain = !window.location.pathname.startsWith("/admin");
    if (options.clientId) {
      req.headers.set("clientId", options.clientId.toString());
    }
    req.headers.set("requestscope", options?.reqScope ?? "client");
    const places = window.location.pathname.split("/").filter((a) => !!a && !isNaN(a as never));
    if (!options.noClientId && isMain && !options?.clientId && places.length) {
      req.headers.set("clientId", places[0]!);
    }

    const authHandler$ = options.noAuth
      ? fromFetch(req)
      : getAuthService()
          .getTokens()
          .pipe(
            switchMap((tokens) => {
              if (!tokens) {
                getAuthService().redirectToLogin();
                return of(null);
              }
              req.headers.set("Authorization", `Bearer ${tokens.accessTokenString}`);
              return fromFetch(req);
            }),
          );

    return authHandler$.pipe(
      switchMap((res) => {
        if (!res) return of(null);

        if (res.status === 204) {
          return of(null) as Observable<Res>;
        } else if (res.ok) {
          if (!options?.res || options?.res === "json") {
            return from(res.json()) as Observable<Res>;
          } else if (options?.res === "text") {
            return from(res.text()) as Observable<Res>;
          } else if (options?.res === "blob") {
            return from(res.blob()) as Observable<Res>;
          } else {
            return of(null);
          }
        } else {
          if (res.status === 401) {
            getAuthService().logout();
            return of(null);
          }
          return from(res.text()).pipe(
            switchMap((text) => {
              const err = getApiError(text, res);
              let res$: Observable<Res | null> | null = null;
              if (options?.errorHandlers && err.typename && !!options?.errorHandlers[err.typename]) {
                options.errorHandlers[err.typename]!(err.detail);
              } else if (options?.errorHandlers && !!options?.errorHandlers[`${res.status}`]) {
                options.errorHandlers[`${res.status}`]!(err);
              } else if (options?.errorHandlers && options?.errorHandlers["*"]) {
                options.errorHandlers["*"](err);
              } else if (options?.errorHandler) {
                res$ = options.errorHandler(res, err) ?? null;
              } else {
                //TODO make Detail component with traceId and message
                toast({
                  severity: "error",
                  summary: "An error has occured",
                  detail: err.message || err.statusText || "",
                  sticky: true,
                });
              }
              !options?.nullOnError && cancelToken$.next(true);
              return (res$ as never) || of(null);
            }),
          );
        }
      }),
      takeUntil(cancelToken$),
      catchError((err) => {
        const captureId = captureException(err);
        toast({
          severity: "error",
          summary: "An error has occured",
          sticky: true,
          detail: `Error ID: ${captureId}`,
        });
        return of(null);
      }),
    );
  };
}

const getApiError = (text: string, res: Response) => {
  const traceId = res.headers.get("x-error-trace-id") ?? undefined;
  try {
    const detail = JSON.parse(text);
    return new ApiError(res.statusText, res.status, detail, traceId);
  } catch (e) {
    return new ApiError(
      res.statusText,
      res.status,
      {
        message: text,
      },
      traceId,
    );
  }
};

export const isApiError = (err: unknown): err is ApiError => {
  return err instanceof ApiError;
};

export class ApiError {
  public readonly typename: string | null;
  public readonly detail: any;
  public readonly message?: string | null;

  constructor(
    public readonly statusText: string,
    public readonly status: number,

    detail: any,
    public readonly traceId?: string,
  ) {
    this.status = status;
    this.detail = detail;
    this.typename = detail.typename || null;

    this.message = detail?.message || detail?.error?.message || detail?.statusText;
  }
}

let apiService: ApiService | null = null;

export const getApiService = (initOptions: ReqOptions = {}) => {
  if (!apiService) {
    apiService = new ApiService(initOptions);
  }
  return apiService;
};
